import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { 
  Building, 
  Phone, 
  Globe, 
  MapPin, 
  Search, 
  CheckCircle,
  UtensilsCrossed,
  ShoppingBag,
  Wrench,
  Heart,
  Sparkles,
  Briefcase,
  Hotel,
  Gamepad2,
  AlertCircle,
  Clock,
  X
} from "lucide-react";
import GooglePlacesMapViewer from "@/components/ui/google-places-map-viewer";
import { BusinessFormValues, OnboardingData } from "../../business/CreateBusinessTab";
import { BusinessCategory } from '@/lib/integrations/google-places-utils';
import { getBrowserTimezone, isValidTimezone, getTimezoneIndicator } from '@/lib/core/timezone-utils';

// Common timezones for easy selection
const COMMON_TIMEZONES = [
  'Europe/Bratislava',
  'Europe/Prague',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Amsterdam',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Budapest',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Lisbon',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Stockholm',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Dubai',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'UTC'
];

// Business type categories for display
const BUSINESS_CATEGORY_INFO = {
  [BusinessCategory.RESTAURANT]: {
    icon: UtensilsCrossed,
    title: "Restaurant & Food Service",
    description: "Restaurants, cafes, bars, and food establishments",
    color: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
    features: ["Online Reviews", "Table Management", "Menu QR Codes", "Loyalty Programs"]
  },
  [BusinessCategory.RETAIL]: {
    icon: ShoppingBag,
    title: "Retail & Shopping",
    description: "Stores, shops, and retail establishments",
    color: "bg-primary/10 text-primary",
    features: ["Product Reviews", "Inventory Management", "Loyalty Programs", "Promotions"]
  },
  [BusinessCategory.SERVICE]: {
    icon: Wrench,
    title: "Service Business",
    description: "Professional services and skilled trades",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
    features: ["Appointment Booking", "Service Reviews", "Time Tracking", "Customer Communication"]
  },
  [BusinessCategory.HEALTH]: {
    icon: Heart,
    title: "Healthcare & Medical",
    description: "Medical practices, clinics, and health services",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    features: ["Patient Reviews", "Appointment Scheduling", "Health Compliance", "Medical Records"]
  },
  [BusinessCategory.BEAUTY]: {
    icon: Sparkles,
    title: "Beauty & Wellness",
    description: "Salons, spas, and beauty services",
    color: "bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400",
    features: ["Appointment Booking", "Before/After Photos", "Service Packages", "Loyalty Programs"]
  },
  [BusinessCategory.PROFESSIONAL]: {
    icon: Briefcase,
    title: "Professional Services",
    description: "Legal, financial, and consulting services",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
    features: ["Client Reviews", "Document Management", "Consultation Tracking", "Referral Programs"]
  },
  [BusinessCategory.HOSPITALITY]: {
    icon: Hotel,
    title: "Hospitality & Travel",
    description: "Hotels, lodging, and travel services",
    color: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    features: ["Booking System", "Guest Reviews", "Concierge Services", "Local Recommendations"]
  },
  [BusinessCategory.ENTERTAINMENT]: {
    icon: Gamepad2,
    title: "Entertainment & Recreation",
    description: "Entertainment venues and recreational activities",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    features: ["Event Management", "Ticket Sales", "Membership Programs", "Photo Sharing"]
  },
  [BusinessCategory.OTHER]: {
    icon: Building,
    title: "Other Business",
    description: "General business establishment",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
    features: ["Customer Reviews", "Basic Analytics", "Contact Management", "Social Media"]
  }
};

interface BusinessDetailsStepProps {
  form: UseFormReturn<BusinessFormValues>;
  onboardingData: OnboardingData;
  setOnboardingData: React.Dispatch<React.SetStateAction<OnboardingData>>;
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
  showManualForm?: boolean;
  setShowManualForm?: React.Dispatch<React.SetStateAction<boolean>>;
}

const BusinessDetailsStep: React.FC<BusinessDetailsStepProps> = ({
  form,
  onboardingData,
  setOnboardingData,
  onPlaceSelect,
  showManualForm = false,
  setShowManualForm,
}) => {
  const [placeConfirmed, setPlaceConfirmed] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);

  const handlePlaceConfirmed = () => {
    setPlaceConfirmed(true);
  };

  const handleStartOver = () => {
    setOnboardingData(prev => ({
      ...prev,
      businessInfo: null,
      businessDetails: {
        name: "",
        description: "",
        address: "",
        website: "",
        phone: "",
        google_place_id: "",
      },
    }));
    setPlaceConfirmed(false);
    setShowManualForm?.(false);
    setSelectedPlace(null);
    form.reset();
  };

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    setSelectedPlace(place);
    onPlaceSelect(place);
  };

  const handleClearSelection = () => {
    setSelectedPlace(null);
  };

  // Helper function to format opening hours nicely
  const formatOpeningHoursDisplay = (openingHours: any): React.ReactNode => {
    if (!openingHours) {
      return <span className="text-gray-500">Hours not available</span>;
    }

    // Check if we have weekday_text
    if (openingHours.weekday_text && Array.isArray(openingHours.weekday_text)) {
      return (
        <div className="space-y-1">
          {openingHours.weekday_text.map((dayText: string, index: number) => (
            <div key={index} className="text-sm">
              <span className="font-medium">{dayText.split(':')[0]}:</span>
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {dayText.split(':').slice(1).join(':').trim() || 'Closed'}
              </span>
            </div>
          ))}
        </div>
      );
    }

    // Fallback to periods format
    if (openingHours.periods && openingHours.periods.length > 0) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const hoursMap: Record<number, {open: string, close: string}> = {};
      
      openingHours.periods.forEach((period: any) => {
        if (period.open && period.open.time) {
          const day = period.open.day;
          const openTime = formatTime(period.open.time);
          const closeTime = period.close ? formatTime(period.close.time) : '24:00';
          hoursMap[day] = { open: openTime, close: closeTime };
        }
      });

      return (
        <div className="space-y-1">
          {dayNames.map((dayName, index) => (
            <div key={index} className="text-sm">
              <span className="font-medium">{dayName}:</span>
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {hoursMap[index] ? `${hoursMap[index].open} - ${hoursMap[index].close}` : 'Closed'}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-gray-500">Hours not available</span>;
  };

  // Helper to format time from Google format (e.g., "0900" -> "9:00")
  const formatTime = (timeString: string): string => {
    if (!timeString || timeString.length !== 4) return timeString;
    const hours = parseInt(timeString.substring(0, 2));
    const minutes = timeString.substring(2, 4);
    return `${hours}:${minutes}`;
  };

  return (
    <div className="space-y-4">
      
      <Form {...form}>
        <div className="space-y-4">
          {/* Google Places Search Section */}
          {!showManualForm && !placeConfirmed && (
            <div className="space-y-3">
              <div className="border rounded-lg p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Side - Search and Map */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <Search className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <h3 className="text-sm font-medium">
                        Find Your Business on Google
                      </h3>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      Search and select your business to auto-fill details
                    </p>
                    <GooglePlacesMapViewer
                      onPlaceSelect={handlePlaceSelect}
                      onPlaceConfirm={handlePlaceConfirmed}
                      placeholder="Search for your business (e.g., 'Starbucks New York')"
                      height="300px"
                      showSearchInput={true}
                      selectedPlace={selectedPlace}
                      onClearSelection={handleClearSelection}
                    />
                  </div>

                  {/* Right Side - Business Details */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <Building className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <h3 className="text-sm font-medium">
                        Business Details
                      </h3>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      Selected business information will appear here
                    </div>
                    {selectedPlace ? (
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <CardTitle className="text-base leading-tight">
                                  {selectedPlace.name || 'Unknown Business'}
                                </CardTitle>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  {selectedPlace.formatted_address || 'No address available'}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleClearSelection}
                              className="shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-3">
                          {/* Business Types */}
                          {selectedPlace.types && selectedPlace.types.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">Business Types</h4>
                              <div className="flex flex-wrap gap-1">
                                {selectedPlace.types.slice(0, 4).map((type, index) => (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {type.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                                {selectedPlace.types.length > 4 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{selectedPlace.types.length - 4} more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Contact Information */}
                          <div className="grid grid-cols-1 gap-2">
                            {selectedPlace.website && (
                              <div className="flex items-center space-x-2">
                                <Globe className="w-4 h-4 text-gray-400" />
                                <a 
                                  href={selectedPlace.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  {selectedPlace.website}
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Opening Hours */}
                          {selectedPlace.opening_hours && (
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center space-x-2">
                                <Clock className="w-4 h-4" />
                                <span>Opening Hours</span>
                              </h4>
                              <div className="text-xs">
                                {formatOpeningHoursDisplay(selectedPlace.opening_hours)}
                              </div>
                            </div>
                          )}

                        </CardContent>
                      </Card>
                    ) : (
                      <div className="min-h-[300px] border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                        <div className="text-center text-gray-500 dark:text-gray-400">
                          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Select a business from the map to see details here</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center">
              </div>
            </div>
          )}


          {/* Manual Form Section */}
          {(showManualForm || placeConfirmed) && (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-medium">
                  {showManualForm ? "Business Details" : "Review & Edit Details"}
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={showManualForm ? () => setShowManualForm?.(false) : handleStartOver}
                >
                  {showManualForm ? "Back to Search" : "Start Over"}
                </Button>
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Input 
                          className="pl-10" 
                          placeholder="My Coffee Shop" 
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            setOnboardingData(prev => ({
                              ...prev,
                              businessDetails: {
                                ...prev.businessDetails,
                                name: e.target.value,
                              },
                            }));
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Input 
                          className="pl-10" 
                          placeholder="123 Main Street, City, State, Country" 
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            setOnboardingData(prev => ({
                              ...prev,
                              businessDetails: {
                                ...prev.businessDetails,
                                address: e.target.value,
                              },
                            }));
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Input 
                          className="pl-10" 
                          placeholder="https://www.mybusiness.com" 
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            setOnboardingData(prev => ({
                              ...prev,
                              businessDetails: {
                                ...prev.businessDetails,
                                website: e.target.value,
                              },
                            }));
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Business Category Selection */}
              <div>
                <label className="text-sm font-medium">Business Category</label>
                <Select
                  value={onboardingData.businessInfo?.businessCategory || ''}
                  onValueChange={(value) => {
                    setOnboardingData(prev => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo,
                        businessCategory: value as BusinessCategory,
                        name: prev.businessDetails.name,
                        address: prev.businessDetails.address,
                        phone: prev.businessDetails.phone,
                        website: prev.businessDetails.website,
                        placeId: '',
                        openingHours: prev.businessInfo?.openingHours || [],
                        businessTypes: [value]
                      }
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your business category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BUSINESS_CATEGORY_INFO).map(([key, category]) => {
                      const Icon = category.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center space-x-2">
                            <Icon className="w-4 h-4" />
                            <span>{category.title}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Business Timezone Selection - Required for proper shift scheduling */}
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Business Timezone
                  <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select the timezone where your business operates. This ensures shifts display at the correct times.
                </p>
                <Select
                  value={onboardingData.businessDetails?.timezone || getBrowserTimezone()}
                  onValueChange={(value) => {
                    setOnboardingData(prev => ({
                      ...prev,
                      businessDetails: {
                        ...prev.businessDetails,
                        timezone: value,
                      }
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your business timezone">
                      {onboardingData.businessDetails?.timezone ? (
                        <span className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          {onboardingData.businessDetails.timezone} ({getTimezoneIndicator(onboardingData.businessDetails.timezone)})
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          {getBrowserTimezone()} ({getTimezoneIndicator(getBrowserTimezone())}) - Auto-detected
                        </span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {/* Browser timezone first if not in common list */}
                    {!COMMON_TIMEZONES.includes(getBrowserTimezone()) && (
                      <SelectItem value={getBrowserTimezone()}>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          <span>{getBrowserTimezone()} ({getTimezoneIndicator(getBrowserTimezone())})</span>
                          <span className="text-xs text-muted-foreground">- Your timezone</span>
                        </div>
                      </SelectItem>
                    )}
                    {COMMON_TIMEZONES.map(tz => (
                      <SelectItem key={tz} value={tz}>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          <span>{tz} ({getTimezoneIndicator(tz)})</span>
                          {tz === getBrowserTimezone() && (
                            <span className="text-xs text-muted-foreground">- Your timezone</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Opening Hours */}
              <div>
                <label className="text-sm font-medium mb-3 block">Opening Hours</label>
                <div className="grid grid-cols-7 gap-2 text-xs">
                  {/* Header Row */}
                  <div className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-2">Day</div>
                  <div className="text-center font-semibold text-gray-700 dark:text-gray-200 text-sm col-span-2 mb-2">Opening</div>
                  <div className="text-center font-semibold text-gray-700 dark:text-gray-200 text-sm col-span-2 mb-2">Closing</div>
                  <div className="text-center font-semibold text-gray-700 dark:text-gray-200 text-sm mb-2">Open 24h</div>
                  <div className="text-center font-semibold text-gray-700 dark:text-gray-200 text-sm mb-2">Closed</div>

                  {/* Hour/Minute Labels Row */}
                  <div></div>
                  <div className="flex justify-center text-gray-500 dark:text-gray-400 text-xs mb-2 col-span-2">
                    <div className="w-full text-center">Hour</div>
                    <div className="w-full text-center">Min</div>
                  </div>
                  <div className="flex text-gray-500 dark:text-gray-400 text-xs mb-2 col-span-2">
                    <div className="w-full text-center">Hour</div>
                    <div className="w-full text-center">Min</div>
                  </div>
                  <div></div>
                  <div></div>

                  {/* Days of week rows */}
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                    const dayHours = onboardingData.businessInfo?.openingHours?.find(h => h.day_of_week === day);
                    const openingTime = dayHours?.opening_time || '09:00:00';
                    const closingTime = dayHours?.closing_time || '17:00:00';
                    const openingHour = openingTime.split(':')[0];
                    const openingMinute = openingTime.split(':')[1];
                    const closingHour = closingTime.split(':')[0];
                    const closingMinute = closingTime.split(':')[1];
                    
                    const updateHours = (updates: Partial<any>) => {
                      setOnboardingData(prev => {
                        const existingHours = prev.businessInfo?.openingHours || [];
                        const otherDays = existingHours.filter(h => h.day_of_week !== day);
                        const currentDay = existingHours.find(h => h.day_of_week === day);
                        
                        const newHours = [...otherDays, {
                          day_of_week: day,
                          opening_time: currentDay?.opening_time || '09:00:00',
                          closing_time: currentDay?.closing_time || '17:00:00',
                          is_closed_all_day: currentDay?.is_closed_all_day || false,
                          is_24_hours: currentDay?.is_24_hours || false,
                          ...updates
                        }];
                        
                        return {
                          ...prev,
                          businessInfo: {
                            ...prev.businessInfo,
                            name: prev.businessDetails.name,
                            address: prev.businessDetails.address,
                            phone: prev.businessDetails.phone,
                            website: prev.businessDetails.website,
                            placeId: '',
                            businessCategory: prev.businessInfo?.businessCategory || BusinessCategory.OTHER,
                            businessTypes: prev.businessInfo?.businessTypes || [],
                            openingHours: newHours
                          }
                        };
                      });
                    };
                    
                    return (
                      <React.Fragment key={day}>
                        {/* Day label */}
                        <div className="text-sm font-medium py-2">{day}</div>
                        
                        {/* Opening time selects */}
                        <Select
                          value={openingHour}
                          onValueChange={(value) => updateHours({ opening_time: `${value}:${openingMinute}:00` })}
                          disabled={dayHours?.is_closed_all_day || dayHours?.is_24_hours}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                              <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select
                          value={openingMinute}
                          onValueChange={(value) => updateHours({ opening_time: `${openingHour}:${value}:00` })}
                          disabled={dayHours?.is_closed_all_day || dayHours?.is_24_hours}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['00', '15', '30', '45'].map(minute => (
                              <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Closing time selects */}
                        <Select
                          value={closingHour}
                          onValueChange={(value) => updateHours({ closing_time: `${value}:${closingMinute}:00` })}
                          disabled={dayHours?.is_closed_all_day || dayHours?.is_24_hours}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                              <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select
                          value={closingMinute}
                          onValueChange={(value) => updateHours({ closing_time: `${closingHour}:${value}:00` })}
                          disabled={dayHours?.is_closed_all_day || dayHours?.is_24_hours}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['00', '15', '30', '45'].map(minute => (
                              <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* 24 Hours checkbox */}
                        <div className="flex justify-center">
                          <Checkbox
                            checked={dayHours?.is_24_hours || false}
                            onCheckedChange={(checked) => updateHours({ 
                              is_24_hours: checked, 
                              is_closed_all_day: false,
                              opening_time: '00:00:00',
                              closing_time: '23:59:00'
                            })}
                            className="bg-gray-50 dark:bg-gray-800"
                          />
                        </div>
                        
                        {/* Closed All Day checkbox */}
                        <div className="flex justify-center">
                          <Checkbox
                            checked={dayHours?.is_closed_all_day || false}
                            onCheckedChange={(checked) => updateHours({ 
                              is_closed_all_day: checked, 
                              is_24_hours: false,
                              opening_time: null,
                              closing_time: null
                            })}
                            className="bg-gray-50 dark:bg-gray-800"
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </Form>
    </div>
  );
};

export default BusinessDetailsStep;